"use client";

import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Children, useMemo } from "react";

export default function CenteredListOrGrid({
    children,
    gridClass = "grid gap-8 sm:grid-cols-2 md:grid-cols-3",
    maxCardWidth = "max-w-xs",
    ariaLabel = "List",
    stagger = 0.07,
    as = "div", // 'div' | 'ul' | 'ol'
    className = "",
    ...props
}) {
    // Preserve React keys with Children.toArray, then drop falsy
    const items = useMemo(
        () => Children.toArray(children).filter(Boolean),
        [children]
    );
    if (!items.length) return null;

    const prefersReducedMotion = useReducedMotion();

    const containerVariants = prefersReducedMotion
        ? {}
        : { show: { transition: { staggerChildren: stagger } } };

    const itemVariants = prefersReducedMotion
        ? {}
        : {
              hidden: { opacity: 0, y: 24 },
              show: {
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.44, ease: [0.23, 1, 0.32, 1] },
              },
              exit: { opacity: 0, y: 24, transition: { duration: 0.22 } },
          };

    // Pick correct motion elements
    const MotionContainer =
        as === "ul" ? motion.ul : as === "ol" ? motion.ol : motion.div;
    const MotionItem = as === "ul" || as === "ol" ? motion.li : motion.div;

    // Only apply ARIA list roles when not using real list elements
    const role = as === "ul" || as === "ol" ? undefined : "list";
    const itemRole = role === "list" ? "listitem" : undefined;

    // Under 3 items → center them in a row
    if (items.length < 3) {
        return (
            <MotionContainer
                className={`flex justify-center gap-8 ${className}`}
                role={role}
                aria-label={ariaLabel}
                variants={containerVariants}
                initial={prefersReducedMotion ? undefined : "hidden"}
                animate={prefersReducedMotion ? undefined : "show"}
                {...props}
            >
                <AnimatePresence>
                    {items.map((child, idx) => (
                        <MotionItem
                            key={child.key ?? idx}
                            className={`${maxCardWidth} w-full`}
                            variants={itemVariants}
                            initial={
                                prefersReducedMotion ? undefined : "hidden"
                            }
                            animate={prefersReducedMotion ? undefined : "show"}
                            exit={prefersReducedMotion ? undefined : "exit"}
                            layout
                            role={itemRole}
                        >
                            {child}
                        </MotionItem>
                    ))}
                </AnimatePresence>
            </MotionContainer>
        );
    }

    // Grid layout
    return (
        <MotionContainer
            className={`${gridClass} ${className}`}
            role={role}
            aria-label={ariaLabel}
            variants={containerVariants}
            initial={prefersReducedMotion ? undefined : "hidden"}
            animate={prefersReducedMotion ? undefined : "show"}
            {...props}
        >
            <AnimatePresence>
                {items.map((child, idx) => (
                    <MotionItem
                        key={child.key ?? idx}
                        variants={itemVariants}
                        initial={prefersReducedMotion ? undefined : "hidden"}
                        animate={prefersReducedMotion ? undefined : "show"}
                        exit={prefersReducedMotion ? undefined : "exit"}
                        layout
                        role={itemRole}
                        className={maxCardWidth}
                    >
                        {child}
                    </MotionItem>
                ))}
            </AnimatePresence>
        </MotionContainer>
    );
}
